import React from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

interface CutoffDateSelectorProps {
  cutoffDate?: Date;
  onCutoffDateChange: (date?: Date) => void;
  className?: string;
}

export const CutoffDateSelector: React.FC<CutoffDateSelectorProps> = ({
  cutoffDate,
  onCutoffDateChange,
  className = "",
}) => {
  const handleQuickSelect = (
    type: "today" | "end-of-week" | "end-of-month"
  ) => {
    const today = new Date();

    switch (type) {
      case "today":
        onCutoffDateChange(today);
        break;
      case "end-of-week": {
        const endOfWeek = new Date();
        endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
        onCutoffDateChange(endOfWeek);
        break;
      }
      case "end-of-month": {
        const endOfMonth = new Date();
        endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0);
        onCutoffDateChange(endOfMonth);
        break;
      }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700">
        📅 Fecha de Corte Histórica
      </label>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={cutoffDate ? "default" : "outline"}
            className={`w-full justify-start text-left font-normal ${
              !cutoffDate && "text-muted-foreground"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            {cutoffDate ? (
              <span className="truncate font-medium">
                <span className="hidden sm:inline">
                  {format(cutoffDate, "dd 'de' MMMM 'de' yyyy", { locale: es })}
                </span>
                <span className="sm:hidden">
                  {format(cutoffDate, "dd/MM/yyyy", { locale: es })}
                </span>
              </span>
            ) : (
              <span className="truncate">
                <span className="hidden sm:inline">
                  Seleccionar fecha de corte histórica
                </span>
                <span className="sm:hidden">Seleccionar fecha</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={cutoffDate}
            onSelect={onCutoffDateChange}
            initialFocus
            locale={es}
            className="rounded-md border"
          />

          <div className="border-t p-2 sm:p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-1 sm:gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleQuickSelect("today")}
                  className="flex-1 text-xs sm:flex-none"
                >
                  Hoy
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleQuickSelect("end-of-week")}
                  className="flex-1 text-xs sm:flex-none"
                >
                  <span className="hidden sm:inline">Fin de semana</span>
                  <span className="sm:hidden">Fin semana</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleQuickSelect("end-of-month")}
                  className="flex-1 text-xs sm:flex-none"
                >
                  <span className="hidden sm:inline">Fin de mes</span>
                  <span className="sm:hidden">Fin mes</span>
                </Button>
              </div>

              {cutoffDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCutoffDateChange(undefined)}
                  className="mt-2 w-full text-xs sm:mt-0 sm:w-auto"
                >
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {cutoffDate && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs">
          <div className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500"></div>
          <span className="text-green-700">
            <span className="hidden sm:inline">
              Mostrando historial completo hasta el{" "}
              <span className="font-medium">
                {format(cutoffDate, "dd 'de' MMMM", { locale: es })}
              </span>
            </span>
            <span className="sm:hidden">
              Historial hasta{" "}
              <span className="font-medium">
                {format(cutoffDate, "dd/MM", { locale: es })}
              </span>
            </span>
          </span>
        </div>
      )}
    </div>
  );
};
